
import _ from 'lodash'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import compiler from 'shared_src/igao/compiler'

import igaoAdmin from '../../lib/igaoAdmin'
import TemplateGenerator, {
  TableHead
} from '../igao-template-generator/template-generator'
import { DynamicIGAO } from '../igao/components/dynamic-igao'
import {
  capitalize,
  useStateCallback,
  headerType
} from '../igao/utils'
import { PlainButton } from '../shared/Button'
import { FriendlyDateTime } from '../shared/Date/friendly-date-time'
import { Divider } from '../shared/Divider'
import { openUrlInNewTab } from '../shared/DownloadLink/DownloadLink'
import MagicGrid from '../shared/MagicGrid'
import {
  Button,
  DraftButton,
  ListItem,
  PortedModal,
  CircularProgress
} from '../shared/NarusMaterial'

import ExportIcon from './components/ExportIcon'
import ResetIcon from './components/ResetIcon'

const pageComponent = (page, name) => {
  const pageLabel = !name
    ? page
      .split('_')
      .map(val => capitalize(val))
      .join(' ')
    : name
  return <ListItem url={`/admin/igao/${page.toLowerCase()}`}>{name || pageLabel}</ListItem>
}

const nameMap = {
  insurances: 'Insurance',
  family_support: 'Family | Support',
  expectation: 'Expectations',
  post_polst: 'POST/POLST',
  dnr: 'DNR',
  esas: 'ESAS +',
  csr: 'CSR'
}

function Igao (props) {
  const { pathname } = useLocation()
  const parts = pathname.split('/')

  let path = parts[parts.length - 1]
  if (path === 'igao') {
    path = undefined
  }

  const [ pageData, setPageData ] = useStateCallback(null)
  const [ metaData, setMetaData ] = useState(null)
  const [ loading, setLoading ] = useState(null)
  const [ draft, setDraft ] = useState({ getting: true })
  const [ modal, setModal ] = useState(null)
  const [ loadingDefault, setLoadingDefault ] = useState(null)

  const removePreviewData = data => {
    if (!data?.[path]) {
      return data
    }
    const _data = _.cloneDeep(data)
    _data[path].items = _data?.[path]?.items?.map(item => {
      const _item = { ...item }
      if (!headerType.includes(item.type)) _item.answer = null
      _item.issues = []
      _item.relations = _item.relations?.map(rel => {
        const _rel = { ...rel }
        if (_rel.values) {
          _rel.values = []
        } else {
          _rel.value = ''
        }
        return _rel
      })
      return _item
    })
    _data[path].lists = []
    return _data
  }

  const setDefault = useCallback(_path => {
    setLoadingDefault(_path)
    igaoAdmin.setDefault({ path: _path }).then(data => {
      setMetaData(data)
      setTimeout(() => {
        setLoadingDefault(null)
      }, 500)
    })
  }, [])

  const onResetToDefault = useCallback(
    _path => {
      setModal({
        title:
          'Are you sure you want to reset this questionnaire to default version?',
        open: true,
        actions: [
          <Button
            key={1}
            label='Cancel'
            onClick={() => setModal(null)}
            grey
          />,
          <Button
            key={2}
            label='Reset'
            onClick={() => {
              setDefault(_path)
              setModal(null)
            }}
          />
        ]
      })
    },
    [ setDefault ]
  )

  const headers = useMemo(
    () => [
      {
        displayFn: r => pageComponent(r.path, nameMap[r.path]),
        title: 'Page',
        key: 'path'
      },
      {
        displayFn: r =>
          (r.created_at && (
            <FriendlyDateTime
              full
              datetime={r.created_at}
            />
          )) ||
          '-',
        title: 'Last Updated',
        key: 'created_at'
      },
      {
        displayFn: r => r.name || '-',
        title: 'Updated by',
        key: 'name'
      },
      {
        displayFn: r => r.email || '-',
        title: 'Email',
        key: 'email'
      },
      {
        displayFn: r => (
          <button onClick={() => onResetToDefault(r.path)}>
            <ResetIcon />
          </button>
        ),
        title: 'Action',
        performSort: false,
        sortBy: false
      }
    ],
    [ onResetToDefault ]
  )

  const fetchData = useCallback(() => {
    igaoAdmin.get({ path }).then(({ data }) => {
      setPageData(() => ({ ...data }))
    })
  }, [ path, setPageData ])

  const publish = () => {
    if (path) {
      setDraft({ publishing: true })
      setLoading(true)
      const data = removePreviewData(pageData)
      igaoAdmin
        .publish({
          data: {
            [path]: {
              'items': data[path].items,
              'title': data[path].title,
              'tab-title': data[path]['tab-title']
            }
          },
          path
        })
        .then(response => {
          setPageData({ ...response?.data })
          setDraft({
            get: true,
            data
          })
        })
        .catch(error => {
          setDraft({ error })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const revertPage = () => {
    if (path) {
      igaoAdmin.revert({ path }).then(({ data }) => {
        setPageData(() => ({ ...data }))
        setDraft({ get: true })
      })
    }
  }

  const onCancel = () => {
    setModal({
      title:
        'Are you sure you want to cancel all changes done on this questionnaire?',
      open: true,
      actions: [
        <Button
          key={1}
          label='Cancel'
          onClick={() => setModal(null)}
          grey
        />,
        <Button
          key={2}
          label='Discard'
          onClick={() => {
            revertPage()
            setModal(null)
          }}
        />
      ]
    })
  }

  const onPublish = () => {
    setModal({
      title: 'Are you sure you want to publish your changes?',
      open: true,
      actions: [
        <Button
          key={1}
          label='Cancel'
          onClick={() => setModal(null)}
          grey
        />,
        <Button
          key={2}
          label='Publish'
          onClick={() => {
            publish()
            setModal(null)
          }}
        />
      ]
    })
  }

  const onExport = () => {
    setModal({
      title:
        'Are you sure you want to export all questionnaires in the JSON format? The .json file will be downloaded in your browser.',
      open: true,
      actions: [
        <Button
          key={1}
          label='Cancel'
          onClick={() => setModal(null)}
          grey
        />,
        <Button
          key={2}
          label='Export'
          onClick={() => {
            openUrlInNewTab('/api/admin/igao/export')
            setModal(null)
          }}
        />
      ]
    })
  }

  const fetchMetaData = () => {
    igaoAdmin.getMetaData().then(data => {
      setMetaData(data)
    })
  }

  const setData = value => {
    setDraft({ setting: true })
    const state = current => {
      const proposed = _.isFunction(value)
        ? value(_.cloneDeep(current))
        : _.cloneDeep(value)
      return compiler.compile({
        current: { data: current },
        proposed: { data: proposed }
      }).data
    }
    setPageData(state, _data => {
      const data = removePreviewData(_data)
      if (data?.[path]?.draft) {
        data[path].draft = false
      }
      const preparedData = {
        data: {
          [path]: {
            'items': data[path].items,
            'title': data[path].title,
            'tab-title': data[path]['tab-title']
          }
        },
        path
      }
      igaoAdmin.set(preparedData)
        .then(current => {
          setPageData(proposedData => {
            const proposed = { data: proposedData }
            const compiled = compiler.compile({
              current,
              proposed
            })
            setDraft({ set: true })
            return compiled.data
          })
        })
    })
  }

  const setPreviewData = value => {
    const state = current =>
      (_.isFunction(value) ? value(_.cloneDeep(current)) : _.cloneDeep(value))
    setPageData(state)
  }

  useEffect(() => {
    if (path) {
      fetchData()
      setDraft({ get: true })
    } else {
      fetchMetaData()
    }
  }, [ fetchData, path ])

  const disabled = !pageData?.[path]?.draft || loading || draft.setting

  // Use an invalid index in order to disable initial sorting
  const [ sortColumnIndex, setSortColumnIndex ] = useState(-1)
  const [ sortDirection, setSortDirection ] = useState('asc')

  const sortColumn = useMemo(
    () => headers?.[sortColumnIndex]?.key,
    [ headers, sortColumnIndex ]
  )

  const sortedMetaData = useMemo(
    () =>
      (sortColumn
        ? metaData
          ?.slice()
          .sort((a, b) =>
            (sortDirection === 'asc'
              ? a[sortColumn]?.localeCompare(b[sortColumn] ?? 0)
              : b[sortColumn]?.localeCompare(a[sortColumn] ?? 0)))
        : metaData?.slice()),
    [ metaData, sortColumn, sortDirection ]
  )

  return (
    <div>
      {!path
        ? metaData && (
          <div style={{ padding: '20px' }}>
            <h1
              style={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <b>Dynamic IGAOs</b>
              <button
                className='export_button'
                onClick={onExport}
              >
                <ExportIcon />
                {' '}
                <span>EXPORT QUESTIONNAIRES</span>
              </button>
            </h1>
            {loadingDefault && <CircularProgress />}
            <MagicGrid
              rows={sortedMetaData}
              sortOn={sortColumnIndex}
              onSort={({ index, direction }) => {
                setSortColumnIndex(index)
                setSortDirection(direction)
              }}
              headers={headers}
              canDelete={false}
              itemName='Options'
            />
          </div>
        )
        : pageData?.[path] && (
          <>
            <TemplateGenerator
              route={props.route}
              page={path}
              data={pageData}
              setPageData={setData}
              setModal={setModal}
            />
            <div
              style={{
                textAlign: 'right',
                padding: '28px 90px 0 0'
              }}
            >
              <DraftButton draft={draft} />
              <PlainButton
                disabled={disabled}
                style={{ marginLeft: '6px' }}
                label='Discard changes'
                btnType='contained'
                onClick={() => onCancel()}
              />
              <PlainButton
                disabled={disabled}
                style={{ marginLeft: '12px' }}
                label='Publish'
                color='primary'
                btnType='contained'
                onClick={() => onPublish()}
              />
            </div>
            <div
              style={{ marginTop: '50px' }}
            >
              <div
                style={{
                  borderTopRightRadius: '8px',
                  borderTopLeftRadius: '8px',
                  padding: '50px 30px 15px',
                  backgroundColor: '#f8f8f8'
                }}
              >
                <TableHead
                  style={{ fontSize: '19px' }}
                  title='Preview'
                />
              </div>
              <Divider />
              <div className='preview'>
                <DynamicIGAO
                  data={pageData}
                  setPageData={setPreviewData}
                  page={path}
                  publish={false}
                />
              </div>
            </div>
          </>
        )}
      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </div>
  )
}

export default Igao
